"use client";

import { useSearchParams } from "next/navigation";
import { useEffect, useMemo, Suspense } from "react";
import dayjs from "dayjs";
import Button from "apps/website/components/base/Button/Button";
import Column from "apps/website/components/layout/Column/Column";
import Container from "apps/website/components/layout/Container/Container";
import Grid from "apps/website/components/layout/Grid/Grid";
import Section from "apps/website/components/layout/Section/Section";
import SectionAnimatedSplit from "apps/website/components/section/SectionAnimatedSplit/SectionAnimatedSplit";
import Spacer from "apps/website/components/layout/Spacer/Spacer";
import Text from "apps/website/components/base/Text/Text";
import {
  legacySizeCollectionMap,
} from "apps/website/components/base/Text/Text.map";
import { useAuth } from "@auth/client-sdk-react";
import {
  useTrackSuccessfulPurchase,
} from "apps/website/hooks/useTrackSuccessfulPurchase";
import { useCatNamesFromQuery } from "apps/website/hooks/useCatNamesFromQuery";
import {
  useCatGenderFromQuery,
} from "apps/website/hooks/useCatGenderFromQuery";
import { useMentionMePopup } from "apps/website/hooks/useMentionMePopup";

import TextBody from "../base/Text/TextBody/TextBody";
import TextTitle from "../base/Text/TextTitle/TextTitle";
import SectionImageFreeFlow from "../section/SectionImageFreeFlow/SectionImageFreeFlow";
import TextSubtitle from "../base/Text/TextSubtitle/TextSubtitle";
import {
  PostCheckoutSurvey,
} from "../feature/PostCheckoutSurvey/PostCheckoutSurvey";
import SovendusBannerComponent from "../feature/Sovendus/Sovendus";

const DATE_FORMAT = "ddd MMM D YYYY";

export const LegacyFreshWelcome = () => {
  const { firstOrder } = useTrackSuccessfulPurchase({ doNotShowMentionMePopup: true });

  const { loggedIn, firstName, lastName, email, old } = useAuth();

  const searchParams = useSearchParams();

  const catNames = useCatNamesFromQuery();
  const catGenders = useCatGenderFromQuery();

  const healthSurveyStartUrl = loggedIn
    ? "/surveys/health/demographic/start"
    : "/login?postLogin=/surveys/health";

  const freshDeliveryDateString = firstOrder?.deliveryDate;
  const litterDeliveryDateString = searchParams.get("litterDeliveryDate");

  const [ freshDeliveryDate, litterDeliveryDate ] = useMemo(() => {
    const parsedFreshDeliveryDate =
      typeof freshDeliveryDateString === "string"
        ? dayjs(freshDeliveryDateString)
        : undefined;

    const parsedLitterDeliveryDate =
      typeof litterDeliveryDateString === "string"
        ? dayjs(litterDeliveryDateString)
        : undefined;

    return [ parsedFreshDeliveryDate, parsedLitterDeliveryDate ];
  }, [ freshDeliveryDateString, litterDeliveryDateString ]);

  const hasLitter = !!litterDeliveryDate;

  const sameDates = freshDeliveryDateString === litterDeliveryDateString;

  const { openMentionMePopup, cleanupMentionMePopup } = useMentionMePopup();

  useEffect(() => () => {
    cleanupMentionMePopup();
  }, []);

  const handleMentionMeClick = () => {
    if (!firstName || !lastName || !email || !old) { return; }

    openMentionMePopup({
      firstname: firstName,
      surname: lastName,
      email,
      customer_id: old,
      phone_number: undefined,
      fullname: lastName ? `${firstName} ${lastName}` : firstName,
      address_country: "United Kingdom",
      implementation: "popup",
      situation: "welcome_page",
      locale: "en_GB" });
  };

  return (
    <>
      <PostCheckoutSurvey />
      <Section theme="brand">
        <Container>
          <Text tag="h1" size={legacySizeCollectionMap.titleLg} align="center" display="title">
            Nice one!
          </Text>
          <Grid>
            <Column spans={{ lg: 8 }} offset={{ lg: 3 }}>
              <Spacer size="lg" />
              <Text align="center" size={{ default: "sm", xl: "xl" }}>
                Our KatKin kitchen is cooking.<br />
                Your box will arrive on:
              </Text>
              <Spacer size="md" />
              <Text tag="div" size={legacySizeCollectionMap.titleMd} align="center" display="subtitle">
                <time dateTime="">
                  { freshDeliveryDate?.format(DATE_FORMAT)?.toUpperCase() || "Arriving Soon" }
                </time>
              </Text>

              { hasLitter && !sameDates && (
                <>
                  <Spacer size="md" />
                  <Text align="center" size={{ default: "sm", xl: "xl" }}>
                    and your litter is arriving on:
                  </Text>
                  <Text tag="div" size={legacySizeCollectionMap.titleMd} align="center" display="subtitle">
                    <time dateTime="">
                      { litterDeliveryDate?.format(DATE_FORMAT)?.toUpperCase() || "Arriving Soon" }
                    </time>
                  </Text>
                </>
              ) }

              <Spacer size="lg" />
              <Text align="center" size={{ default: "sm", xl: "xl" }}>
                Check your inbox for more details
              </Text>
              <Spacer size="lg" />

              <div className="flex justify-center">
                <Button to="/customer" className="mx-2" color="secondary">
                  { loggedIn ? "Explore my account" : "Login" }
                </Button>
              </div>
            </Column>
          </Grid>
        </Container>
      </Section>
      <SectionAnimatedSplit animation="munchAndSnooze" themeTop="brand" themeBottom="default"/>

      <SectionImageFreeFlow theme="light-grey" image={{ image: {
        src: "/images/dev/components/Welcome/RAF_animated.webp",
        width: 350,
        height: 145,
      },
      alt: "refer a friend image",
      justify: "center",
      }}>
        <TextTitle size={2} align={"center"}>
          50% off for you. <br/> A £10 trial for your friend.
        </TextTitle>
        <Spacer size="lg" />
        <TextBody align="center">Help more cats like yours feel the incredible benefits of our healthy, freshly-prepared food. From shinier coats, to louder purrs and faster zoomies.</TextBody>
        <Spacer size="lg" />

        <Button onClick={handleMentionMeClick
        } color="default">Refer a friend</Button>
      </SectionImageFreeFlow>

      <SectionImageFreeFlow theme="dark-alt" direction="reverse" image={{ image: {
        src: "/images/surveys/hero-landing-page-2.webp",
        width: 1287,
        height: 1080,
      },
      alt: "health survey image",
      }}>
        <TextTitle align="center">Join the good health cat study</TextTitle>
        <Spacer size="lg" />
        <TextSubtitle align="center" color="secondary">{ `Take a 10-min health survey for ${catNames.allCatNames}.` }</TextSubtitle>
        <Spacer size="lg" />
        <TextBody align="center">{ `Along with thousands of cat parents across the UK, you will be the first to receive a personalised health report for ${catNames.allCatNames}, giving ${catGenders.catGenderHimHerThem} the best shot at a long life.` }</TextBody>
        <Spacer size="lg" />
        <Button id={healthSurveyStartUrl} withIcon={false} to={healthSurveyStartUrl} color="secondary"> { loggedIn ? "Take part" : "Login to take part" }</Button>
      </SectionImageFreeFlow>
      <Suspense>
        <SovendusBannerComponent/>
      </Suspense>
    </>
  );
};
